/**
 * Hook to fetch and manage user entitlements (subscription + add-ons)
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const useUserEntitlements = () => {
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchEntitlements = useCallback(async () => {
    try {
      console.log('🔍 Fetching entitlements...');
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('enakid_token') || Cookies.get('enakid_token');
      console.log('Token present:', !!token);
      if (!token) {
        console.log('❌ No token found');
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      console.log('📡 Making API call to:', `${API_URL}/api/user/entitlements`);
      const response = await axios.get(`${API_URL}/api/user/entitlements`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('✅ Entitlements received:', response.data);
      setEntitlements(response.data);
      setLoading(false);
    } catch (err) {
      console.error('❌ Error fetching entitlements:', err);
      console.error('Error details:', err.response?.data);
      setError(err.response?.data?.detail || 'Failed to fetch entitlements');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntitlements();
  }, [fetchEntitlements]);

  const hasFeature = useCallback((featureName) => {
    if (!entitlements) return false;
    if (entitlements.is_admin) return true;
    return entitlements.features?.includes(featureName) || false;
  }, [entitlements]);

  const getLimit = useCallback((limitType) => {
    if (!entitlements) return null;
    if (entitlements.is_admin) return { total: -1, current_usage: 0, remaining: -1 };
    return entitlements.limits?.[limitType] || null;
  }, [entitlements]);

  const canCreate = useCallback((limitType) => {
    if (!entitlements) {
      console.log(`canCreate(${limitType}): No entitlements yet`);
      return false;
    }
    if (entitlements.is_admin) return true;
    
    const limit = entitlements.limits?.[limitType];
    if (!limit) {
      console.log(`canCreate(${limitType}): No limit found in entitlements`);
      return false;
    }
    
    // If unlimited (-1), always allow
    if (limit.total === -1) return true;
    
    // Check if under limit
    const canCreate = limit.current_usage < limit.total;
    console.log(`canCreate(${limitType}): ${limit.current_usage} < ${limit.total} = ${canCreate}`);
    return canCreate;
  }, [entitlements]);

  const getUsagePercentage = useCallback((limitType) => {
    if (!entitlements) return 0;
    if (entitlements.is_admin) return 0;
    
    const limit = entitlements.limits?.[limitType];
    if (!limit || limit.total === -1) return 0;
    
    return (limit.current_usage / limit.total) * 100;
  }, [entitlements]);

  return {
    entitlements,
    loading,
    error,
    hasFeature,
    getLimit,
    canCreate,
    getUsagePercentage,
    refetch: fetchEntitlements
  };
};

export default useUserEntitlements;
